import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/cms`;

const CMSFloorPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    sqft: '',
    bedrooms: 3,
    bathrooms: 2,
    garage: '2-Car Garage',
    style: 'Ranch',
    description: '',
    features: '',
    images: '',
    thumbnail: '',
    status: 'active'
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/floor-plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error loading floor plans:', error);
      toast.error('Failed to load floor plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseFloat(formData.bathrooms),
        features: formData.features.split('\n').filter(f => f.trim()),
        images: formData.images.split('\n').filter(i => i.trim()),
      };

      if (editingPlan) {
        await axios.put(`${API}/floor-plans/${editingPlan.id}`, data);
        toast.success('Floor plan updated successfully');
      } else {
        await axios.post(`${API}/floor-plans`, data);
        toast.success('Floor plan created successfully');
      }

      resetForm();
      loadPlans();
    } catch (error) {
      console.error('Error saving floor plan:', error);
      toast.error('Failed to save floor plan');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      sqft: plan.sqft,
      bedrooms: plan.bedrooms,
      bathrooms: plan.bathrooms,
      garage: plan.garage,
      style: plan.style,
      description: plan.description,
      features: plan.features.join('\n'),
      images: plan.images.join('\n'),
      thumbnail: plan.thumbnail,
      status: plan.status
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this floor plan?')) return;

    try {
      await axios.delete(`${API}/floor-plans/${id}`);
      toast.success('Floor plan deleted');
      loadPlans();
    } catch (error) {
      console.error('Error deleting floor plan:', error);
      toast.error('Failed to delete floor plan');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      sqft: '',
      bedrooms: 3,
      bathrooms: 2,
      garage: '2-Car Garage',
      style: 'Ranch',
      description: '',
      features: '',
      images: '',
      thumbnail: '',
      status: 'active'
    });
    setEditingPlan(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-[#262626]">Floor Plans</h2>
          <p className="text-gray-600">Manage your home plans</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#DAA520] hover:bg-[#B8860B] text-white"
        >
          {showForm ? <X className="mr-2" size={20} /> : <Plus className="mr-2" size={20} />}
          {showForm ? 'Cancel' : 'Add Floor Plan'}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingPlan ? 'Edit Floor Plan' : 'New Floor Plan'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Plan Name</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="The Harrison"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Square Feet</label>
                  <Input
                    value={formData.sqft}
                    onChange={(e) => setFormData({...formData, sqft: e.target.value})}
                    placeholder="1,627"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Bedrooms</label>
                  <Input
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({...formData, bedrooms: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Bathrooms</label>
                  <Input
                    type="number"
                    step="0.5"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({...formData, bathrooms: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Garage</label>
                  <Input
                    value={formData.garage}
                    onChange={(e) => setFormData({...formData, garage: e.target.value})}
                    placeholder="2-Car Garage"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Style</label>
                  <Input
                    value={formData.style}
                    onChange={(e) => setFormData({...formData, style: e.target.value})}
                    placeholder="Ranch"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Features (one per line)</label>
                <Textarea
                  value={formData.features}
                  onChange={(e) => setFormData({...formData, features: e.target.value})}
                  rows={4}
                  placeholder="Open kitchen with modern finishes\nSpacious walk-in closet"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Thumbnail Image URL</label>
                <Input
                  value={formData.thumbnail}
                  onChange={(e) => setFormData({...formData, thumbnail: e.target.value})}
                  placeholder="https://..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Additional Images (one URL per line)</label>
                <Textarea
                  value={formData.images}
                  onChange={(e) => setFormData({...formData, images: e.target.value})}
                  rows={3}
                  placeholder="https://image1.jpg\nhttps://image2.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-[#DAA520] hover:bg-[#B8860B]">
                  {loading ? 'Saving...' : editingPlan ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {loading && plans.length === 0 ? (
          <Card><CardContent className="p-12 text-center">Loading...</CardContent></Card>
        ) : plans.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-gray-500">No floor plans yet. Create your first one!</CardContent></Card>
        ) : (
          plans.map((plan) => (
            <Card key={plan.id}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex gap-4 flex-1">
                    <img
                      src={plan.thumbnail}
                      alt={plan.name}
                      className="w-32 h-32 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-[#262626]">{plan.name}</h3>
                        <Badge className={plan.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}>
                          {plan.status}
                        </Badge>
                      </div>
                      <p className="text-gray-600 mb-2">{plan.style} • {plan.sqft} sq ft • {plan.bedrooms} Bed • {plan.bathrooms} Bath</p>
                      <p className="text-sm text-gray-500 line-clamp-2">{plan.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(plan)}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(plan.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default CMSFloorPlans;